import { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { DateFormattedUtils } from '@utriper/core-ui';
import DownloadIcon from '@mui/icons-material/Download';

const VisitTab = ({ visits = [] }) => {
    const { t: translate, i18n } = useTranslation();

    const normalizedVisits = useMemo(() => {
        if (Array.isArray(visits)) return visits;
        return [];
    }, [visits]);

    const hasVisits = normalizedVisits.length > 0;

    // State for column widths (resizable columns)
    const [columns, setColumns] = useState([
        { columnId: 'id', width: 80, resizable: true },
        { columnId: 'timestamp', width: 200, resizable: true },
        { columnId: 'uniqueVisitId', width: 150, resizable: true },
        { columnId: 'url', width: 300, resizable: true },
        { columnId: 'originUrl', width: 300, resizable: true },
        { columnId: 'originId', width: 120, resizable: true },
        { columnId: 'code', width: 150, resizable: true },
        { columnId: 'contractId', width: 120, resizable: true },
    ]);

    // Handle column resize
    const handleColumnResize = useCallback((columnId, width) => {
        setColumns((prevColumns) => prevColumns.map((col) => (col.columnId === columnId ? { ...col, width } : col)));
    }, []);

    // CSV Export function
    const exportToCSV = useCallback(() => {
        if (!hasVisits) return;

        // Define headers
        const headers = ['ID', 'Timestamp', 'Unique Visit ID', 'URL', 'Origin URL', 'Origin ID', 'Code', 'Contract ID'];

        // Convert data to CSV rows
        const csvRows = normalizedVisits.map((visit) => {
            const formattedTimestamp = DateFormattedUtils(visit?.timestamp, i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: 'UTC',
            });

            return [
                visit?.id ?? '',
                formattedTimestamp ?? '',
                visit?.uniqueVisitId ?? '',
                visit?.url ?? '',
                visit?.originUrl ?? '',
                visit?.originId ?? '',
                visit?.code ?? '',
                visit?.contractId ?? '',
            ]
                .map((value) => {
                    // Escape quotes and wrap in quotes if contains comma or newline
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                })
                .join(',');
        });

        // Combine headers and rows
        const csvContent = [headers.join(','), ...csvRows].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `traffic-visits-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [hasVisits, normalizedVisits, i18n.language]);

    const headerRow = useMemo(
        () => ({
            rowId: 'header',
            cells: [
                { type: 'header', text: 'ID' },
                { type: 'header', text: translate('traffic.columns.timestamp') || 'Timestamp' },
                { type: 'header', text: translate('traffic.columns.uniqueVisitId') || 'Unique Visit ID' },
                { type: 'header', text: translate('traffic.columns.url') || 'URL' },
                { type: 'header', text: translate('traffic.columns.originUrl') || 'Origin URL' },
                { type: 'header', text: translate('traffic.columns.originId') || 'Origin ID' },
                { type: 'header', text: translate('traffic.columns.code') || 'Code' },
                { type: 'header', text: translate('traffic.columns.contractId') || 'Contract ID' },
            ],
        }),
        [translate]
    );

    const rows = useMemo(() => {
        if (!hasVisits) return [headerRow];

        const dataRows = normalizedVisits.map((visit, idx) => {
            const formattedTimestamp = DateFormattedUtils(visit?.timestamp, i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: 'UTC',
            });

            return {
                rowId: idx,
                cells: [
                    { type: 'text', text: String(visit?.id ?? '') },
                    { type: 'text', text: formattedTimestamp ?? '' },
                    { type: 'text', text: String(visit?.uniqueVisitId ?? '') },
                    { type: 'text', text: String(visit?.url ?? '') },
                    { type: 'text', text: String(visit?.originUrl ?? '') },
                    { type: 'text', text: String(visit?.originId ?? '') },
                    { type: 'text', text: String(visit?.code ?? '') },
                    { type: 'text', text: String(visit?.contractId ?? '') },
                ],
            };
        });

        return [headerRow, ...dataRows];
    }, [hasVisits, normalizedVisits, i18n.language, headerRow]);

    if (!hasVisits) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: '240px',
                    border: '1px dashed #d1d1d1',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    gap: 1,
                }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#6f6f6f' }}>
                    {translate('traffic.visits_tab') || 'Visits'}
                </Typography>
                <Typography sx={{ color: '#9a9a9a' }}>{translate('traffic.visits_empty') || 'No visits data available'}</Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                    {translate('traffic.visits_tab') || 'Visits'} ({normalizedVisits.length})
                </Typography>
                <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={exportToCSV} disabled={!hasVisits}>
                    {translate('traffic.export_csv') || 'Export to CSV'}
                </Button>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    height: 'calc(100vh - 300px)',
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    '& .rg-root': {
                        '--rg-cell-border-color': '#e0e0e0',
                        '--rg-header-background-color': '#f5f5f5',
                        '--rg-selection-border-color': '#1976d2',
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    },
                }}>
                <ReactGrid
                    rows={rows}
                    columns={columns}
                    onColumnResized={handleColumnResize}
                    enableRangeSelection
                    enableRowSelection
                    enableColumnSelection
                    stickyTopRows={1}
                />
            </Box>
        </Box>
    );
};

VisitTab.displayName = 'VisitTab';

export default VisitTab;
